import React from "react";
import { AlertCircle } from "lucide-react";

/**
 * Class-based error boundary. Hooks can't catch render-phase errors, so this
 * has to stay a class component. Wrap any feature surface (Dashboard,
 * BudgetManager, etc.) so that a single failed parallel fetch doesn't blank
 * the whole screen.
 *
 * Props:
 *   children   — required
 *   fallback   — optional, custom fallback node (skips the default UI)
 *   scope      — optional label used in the default copy ("the dashboard")
 */
class ErrorBoundary extends React.Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Single forward point for future Sentry / RUM wiring.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    const scope = this.props.scope || "this view";
    const isDev = process.env.NODE_ENV !== "production";

    return (
      <div
        role="alert"
        className="min-h-[60vh] flex flex-col items-center justify-center px-8 py-12 text-center gap-3"
      >
        <AlertCircle className="w-14 h-14 text-stone-400" strokeWidth={1.5} />
        <h2 className="text-base font-bold text-stone-900 dark:text-stone-100 mt-1">
          Couldn't load {scope}
        </h2>
        <p className="text-sm text-stone-500 dark:text-stone-400 max-w-sm leading-relaxed">
          Something hiccuped while rendering. Your data is safe — try again.
        </p>
        <button
          type="button"
          onClick={this.reset}
          className="mt-2 px-5 py-2.5 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white text-sm font-bold rounded-xl transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
        >
          Try again
        </button>
        {isDev && (
          <pre className="mt-5 text-[11px] text-stone-400 max-w-md whitespace-pre-wrap break-all font-mono">
            {error.name}: {error.message}
          </pre>
        )}
      </div>
    );
  }
}

export default ErrorBoundary;
